import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase'; 
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth'; 
import { doc, setDoc, getDoc, serverTimestamp, collection, addDoc } from 'firebase/firestore'; 
import Header from '../components/Header';
import Footer from '../components/Footer';

const MCHAT_QUESTIONS = [
  { id: 1, text: "Does your child enjoy being swung, bounced on your knee, etc.?", risk_answer: "no", critical: false },
  { id: 2, text: "Does your child take an interest in other children?", risk_answer: "no", critical: true },
  { id: 3, text: "Does your child like climbing on things, such as stairs?", risk_answer: "no", critical: false },
  { id: 4, text: "Does your child enjoy playing peek-a-boo/hide-and-seek?", risk_answer: "no", critical: false },
  { id: 5, text: "Does your child ever pretend, for example, to talk on the phone or take care of a doll or anything else?", risk_answer: "no", critical: true },
  { id: 6, text: "Does your child ever use his/her index finger to point, to ask for something?", risk_answer: "no", critical: false },
  { id: 7, text: "Does your child ever use his/her index finger to point, to indicate interest in something?", risk_answer: "no", critical: true },
  { id: 8, text: "Can your child play properly with small toys (e.g. cars or bricks) without just mouthing, fiddling, or dropping them?", risk_answer: "no", critical: false },
  { id: 9, text: "Does your child ever bring objects over to you (parent) to show you something?", risk_answer: "no", critical: true },
  { id: 10, text: "Does your child look you in the eye for more than a second or two?", risk_answer: "no", critical: false },
  { id: 11, text: "Does your child ever seem oversensitive to noise? (e.g., plugging ears)", risk_answer: "yes", critical: false },
  { id: 12, text: "Does your child smile in response to your face or your smile?", risk_answer: "no", critical: false },
  { id: 13, text: "Does your child imitate you? (e.g., you make a face-will your child imitate it?)", risk_answer: "no", critical: true },
  { id: 14, text: "Does your child respond to his/her name when you call?", risk_answer: "no", critical: true },
  { id: 15, text: "If you point at a toy across the room, does your child look at it?", risk_answer: "no", critical: true },
  { id: 16, text: "Does your child walk?", risk_answer: "no", critical: false },
  { id: 17, text: "Does your child look at things you are looking at?", risk_answer: "no", critical: false },
  { id: 18, text: "Does your child make unusual finger movements near his/her face?", risk_answer: "yes", critical: false },
  { id: 19, text: "Does your child try to attract your attention to his/her activity?", risk_answer: "no", critical: false },
  { id: 20, text: "Have you ever wondered if your child is deaf?", risk_answer: "yes", critical: false },
];

const MChatAssessment = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [riskLevel, setRiskLevel] = useState('');
  const [riskScore, setRiskScore] = useState(0);
  const [user, setUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const navigate = useNavigate();
  const location = useLocation(); 

  const { 
    childName: initialChildName = '', 
    childsAgeInMonths: initialChildsAgeInMonths = '',
    childGender: initialChildGender = '' 
  } = location.state || {};

  const [childName, setChildName] = useState(initialChildName);
  const [childsAgeInMonths, setChildsAgeInMonths] = useState(initialChildsAgeInMonths);
  const [childGender, setChildGender] = useState(initialChildGender); 
  
  const [childDetailsSubmitted, setChildDetailsSubmitted] = useState(!!(initialChildName && initialChildsAgeInMonths && initialChildGender));
  
  const [pendingAssessmentData, setPendingAssessmentData] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(false); 

  useEffect(() => {
    if (!location.state || !location.state.childName || !location.state.childsAgeInMonths) {
      console.warn('MChatAssessment: Child details not found in location state. Redirecting to free-assessment.');
      navigate('/free-assessment', { replace: true });
      return; 
    }
    setChildDetailsSubmitted(true);
  }, [location.state, navigate]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);
      setIsLoadingAuth(false); 
      if (currentUser && pendingAssessmentData) {
        await handleSaveAndRedirect(pendingAssessmentData, currentUser);
        setPendingAssessmentData(null); 
      }
    });
    return () => unsubscribe();
  }, [pendingAssessmentData, navigate]); 

  const handleAnswer = (answer) => {
    const updatedAnswers = {
      ...answers,
      [MCHAT_QUESTIONS[currentQuestionIndex].id]: answer,
    };
    setAnswers(updatedAnswers);

    if (currentQuestionIndex < MCHAT_QUESTIONS.length - 1) {
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
    } else {
      calculateScoreAndFinalize(updatedAnswers);
    }
  };

  const calculateScoreAndFinalize = async (currentAnswers) => {
    setIsSubmitting(true); 

    let score = 0;
    let criticalFails = 0;
    const failedItemsList = [];
    const criticalItemsFailedList = [];

    MCHAT_QUESTIONS.forEach(q => {
      if (currentAnswers[q.id] === q.risk_answer) {
        score++;
        failedItemsList.push(q.id);
        if (q.critical) {
          criticalFails++;
          criticalItemsFailedList.push(q.id);
        }
      }
    });

    let determinedRiskLevel = 'Low Risk';
    if (score >= 8) {
      determinedRiskLevel = 'High Risk';
    } else if (score >= 3 || criticalFails >= 2) {
      determinedRiskLevel = 'Medium Risk';
    }
    
    setRiskScore(score);
    setRiskLevel(determinedRiskLevel);
    setShowResults(true); 

    const assessmentDataPayload = {
      childName: childName.trim(),
      childsAgeInMonths: parseInt(childsAgeInMonths, 10),
      answers: currentAnswers,
      score,
      riskLevel: determinedRiskLevel,
      failedItems: failedItemsList,
      criticalItemsFailed: criticalItemsFailedList,
      assessmentType: "mchat",
      generatedSummary: "", 
      summaryStatus: "pending_summary", 
      pdfUrl: "", 
    };

    if (user) {
      await handleSaveAndRedirect(assessmentDataPayload, user);
    } else {
      setPendingAssessmentData(assessmentDataPayload);
      setIsSubmitting(false); 
    }
  };

  const handleSaveAndRedirect = async (assessmentData, currentUser) => {
    if (!currentUser || !assessmentData) {
      console.error("User or assessment data missing for save.");
      setIsSubmitting(false);
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Create assessment in the top-level 'assessments' collection
      const assessmentsCollectionRef = collection(db, 'assessments');
      const newAssessmentRef = await addDoc(assessmentsCollectionRef, {
        ...assessmentData,
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        userName: currentUser.displayName || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'completed',
        // Store a reference to the user document
        userRef: doc(db, 'users', currentUser.uid)
      });
      
      console.log("Assessment saved with ID: ", newAssessmentRef.id);
      
      // Also update the user's document to include a reference to this assessment
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        await setDoc(userDocRef, {
          lastAssessment: newAssessmentRef.id,
          lastAssessmentDate: serverTimestamp(),
          updatedAt: serverTimestamp(),
          // Initialize assessments array if it doesn't exist
          assessmentCount: (await getDoc(userDocRef)).data()?.assessmentCount + 1 || 1
        }, { merge: true });
      } catch (userUpdateError) {
        console.warn("Could not update user document with assessment reference:", userUpdateError);
        // Continue even if we can't update the user document
      }
      
      // Store assessment ID in session storage for immediate access
      sessionStorage.setItem('lastAssessmentId', newAssessmentRef.id);
      
      // Navigate to the report page
      navigate(`/app/report/${newAssessmentRef.id}`);

    } catch (error) {
      console.error("Error saving assessment to Firestore: ", error);
      
      // If we don't have a user, try to save the assessment locally
      if (error.code === 'permission-denied' || !currentUser.uid) {
        try {
          const pendingAssessments = JSON.parse(localStorage.getItem('pendingAssessments') || '[]');
          const tempId = `temp_${Date.now()}`;
          pendingAssessments.push({
            ...assessmentData,
            tempId,
            createdAt: new Date().toISOString()
          });
          localStorage.setItem('pendingAssessments', JSON.stringify(pendingAssessments));
          console.log("Assessment saved locally. Will sync when signed in.");
          // Redirect to signup with the temp ID
          navigate(`/auth?redirect=/app/report/${tempId}`);
          return;
        } catch (localError) {
          console.error("Error saving assessment locally:", localError);
        }
      }
      
      alert("There was an error saving your assessment. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleSignIn = async () => {
    if (!pendingAssessmentData) {
        alert("No assessment data to save. Please complete an assessment first.");
        return;
    }
    setIsLoadingAuth(true); 
    setIsSubmitting(true);  
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      // onAuthStateChanged in useEffect will handle saving pendingAssessmentData
      // and navigation if sign-in is successful.
    } catch (error) {
      console.error("Error during Google sign-in: ", error);
      alert(`Sign-in failed: ${error.message}. Please try again.`);
      setIsLoadingAuth(false);
      setIsSubmitting(false); 
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prevIndex => prevIndex - 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResults(false);
    setRiskLevel('');
    setRiskScore(0);
    setPendingAssessmentData(null);
    setIsSubmitting(false);
    setIsLoadingAuth(false);
  };

  if (!childDetailsSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white flex flex-col items-center justify-center p-4">
        <p className="text-lg text-gray-700">Loading child details or missing information...</p>
        <p className="text-sm text-gray-500 mt-2">Please start the assessment from the <a href="/free-assessment" className="text-sky-600 underline">free assessment page</a>.</p>
      </div>
    );
  }

  if (showResults) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-sky-100 to-white flex flex-col items-center p-4 pt-20 pb-20 text-center">
          <h2 className="text-3xl font-bold text-sky-700 mb-6">Assessment Complete!</h2>
          
          {!user && pendingAssessmentData && !isSubmitting && (
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-6 my-4 max-w-md mx-auto rounded-lg shadow-md" role="alert">
              <p className="font-bold text-2xl mb-3">🎉 Report Ready!</p>
              <p className="mb-4 text-lg">Sign up or log in to view your detailed report, AI-generated summary, and download options.</p>
              <button 
                  onClick={handleSignIn}
                  disabled={isLoadingAuth}
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg text-xl shadow-md transform hover:scale-105 transition-transform duration-150 ease-in-out disabled:opacity-70 disabled:cursor-not-allowed"
              >
                  {isLoadingAuth ? 'Connecting...' : 'Sign Up / Log In to View Report'}
              </button>
            </div>
          )}

          {user && (
              isSubmitting ? (
                  <div className="my-6">
                      <p className="text-sky-600 text-xl">Saving your results and preparing your report...</p>
                  </div>
              ) : (
                  <div className="w-full max-w-2xl">
                      <p className="text-xl text-gray-700 mb-1">Score: <span className="font-bold">{riskScore}</span> out of 20</p>
                      <p className="text-xl text-gray-700 mb-6">
                      Your Result: <span className={`font-bold ${riskLevel === 'High Risk' ? 'text-red-600' : riskLevel === 'Medium Risk' ? 'text-orange-500' : 'text-green-600'}`}>
                          {riskLevel === 'High Risk' && '🚨 '}
                          {riskLevel === 'Medium Risk' && '⚠️ '}
                          {riskLevel === 'Low Risk' && '✅ '}
                          {riskLevel}
                      </span>
                      </p>
                      <div className="bg-white p-6 rounded-lg shadow-md max-w-md mx-auto mb-6">
                          <h3 className="text-lg font-semibold mb-2">Next Steps:</h3>
                          <p className="text-gray-700">Your detailed report, including an AI-generated summary and download options, is being prepared.</p>
                          <p className="text-gray-700 mt-2">You will be redirected shortly.</p>
                      </div>
                  </div>
              )
          )}
          
          {isLoadingAuth && !user && (
              <div className="my-6">
                  <p className="text-sky-600 text-xl">Checking your account status...</p>
              </div>
          )}

          <button 
            onClick={handleRestart} 
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg text-lg mt-8 shadow-md transform hover:scale-105 transition-transform duration-150 ease-in-out"
          >
            Start Another Assessment
          </button>
          
          <p className="mt-8 text-xs text-gray-500 text-center max-w-md">
            Disclaimer: This tool is for informational purposes only and not a diagnostic tool. Consult a healthcare professional for diagnosis.
          </p>
        </div>
        <Footer />
      </>
    );
  }

  const currentQuestion = MCHAT_QUESTIONS[currentQuestionIndex];
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white flex flex-col items-center justify-center p-4 pt-20 pb-20"> 
        <div className="bg-white p-6 md:p-8 rounded-lg shadow-xl w-full max-w-lg">
          <div className="text-right text-gray-500 mb-2 font-medium">
            Question {currentQuestionIndex + 1} of {MCHAT_QUESTIONS.length} (For: {childName}, {childsAgeInMonths} months{childGender ? `, ${childGender}` : ''})
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6 dark:bg-gray-700">
            <div 
              className="bg-sky-500 h-2.5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${((currentQuestionIndex + 1) / MCHAT_QUESTIONS.length) * 100}%` }}
            ></div>
          </div>
          <h2 className="text-xl md:text-2xl font-semibold mb-8 text-center text-gray-800">{currentQuestion.text}</h2>
          <div className="flex flex-col sm:flex-row justify-around space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => handleAnswer('yes')}
              className="w-full sm:w-auto flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-lg text-lg md:text-xl shadow-md transform hover:scale-105 transition-transform duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-opacity-75"
            >
              👍 Yes
            </button>
            <button
              onClick={() => handleAnswer('no')}
              className="w-full sm:w-auto flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-4 px-6 rounded-lg text-lg md:text-xl shadow-md transform hover:scale-105 transition-transform duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75"
            >
              👎 No
            </button>
          </div>
          <div className="mt-8 text-center">
          {currentQuestionIndex > 0 && (
              <button 
                  onClick={handlePrevious}
                  className="text-sm text-sky-600 hover:text-sky-800 hover:underline"
              >
                  &larr; Previous Question
              </button>
          )}
          </div>
        </div>
         <p className="mt-8 text-xs text-gray-500 text-center max-w-md">
          Disclaimer: This tool is for informational purposes only and not a diagnostic tool. Consult a healthcare professional for diagnosis.
        </p>
      </div>
      <Footer />
    </>
  );
};

export default MChatAssessment;
